// src/components/About.js
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import AboutBrowserTabs from './AboutBrowserTabs';
import { LanguageContext } from '../App';
import first from '../assets/17.png';
import second from '../assets/16.png';
import third from '../assets/15.png';
import aboutImage from '../assets/hover image.webp';
import step1 from '../assets/1.png'
import step2 from '../assets/2.png'
import step3 from '../assets/3.png'
import step4 from '../assets/4.png'
import step5 from '../assets/5.png'

const About = () => {
    const { language } = useContext(LanguageContext);

    // Translations
    const translations = {
        en: {
            hero: {
                title: "Transform Your Style with AI-Powered Personal Stylist",
                description: "Experience effortless daily styling with an AI assistant that learns your taste, considers your wardrobe, and ensures you're perfectly dressed for any weather or occasion. Save time and unlock your style potential with Style Forecast."
            },
            cta: "Try Style Forecast Now",
            features: {
                title: "Why Choose Style Forecast?",
                cards: [
                    {
                        icon: "🎯",
                        title: "Smart Wardrobe Integration",
                        description: "Upload your existing clothes and let our AI create endless stylish combinations. Get personalized outfit suggestions using your actual wardrobe, maximizing your clothing investment."
                    },
                    {
                        icon: "🌤️",
                        title: "Weather-Perfect Styling",
                        description: "Never be overdressed or underdressed again. Our AI checks real-time weather data and suggests appropriate outfits that keep you comfortable and stylish in any conditions."
                    },
                    {
                        icon: "🎉",
                        title: "Occasion-Ready Outfits",
                        description: "From business meetings to casual weekends, get instantly appropriate outfit combinations. Our AI understands dress codes and social contexts to ensure you always look perfectly suited for any event."
                    },
                    {
                        icon: "👕",
                        title: "Evolving Style Assistant",
                        description: "Your personal AI stylist learns and adapts to your preferences over time. Share feedback, mark favorites, and watch as recommendations become more personalized to your unique taste."
                    }
                ]
            },
            howItWorks: {
                title: "How It Works",
                steps: [
                    {
                        title: "Create Your Profile",
                        description: "Share your style preferences, favorite colors, and fashion inspiration. Our AI learns your unique taste to create personalized recommendations just for you."
                    },
                    {
                        title: "Daily Style Planning",
                        description: "Receive practical outfit combinations each morning, perfectly matched to your schedule and weather. Plan your looks ahead for important events and never stress about what to wear."
                    },
                    {
                        title: "Continuous Style Evolution",
                        description: "Enjoy a stress-free morning routine with perfectly coordinated outfits. Get better recommendations as you use the app. Watch your personal style evolve with AI-powered insights and suggestions."
                    }
                ]
            },
            benefits: {
                title: "Transform Your Daily Style Experience",
                cards: [
                    {
                        title: "Time-Saving Solution",
                        description: "Start your day confidently with ready-to-wear outfit combinations. No more morning wardrobe stress."
                    },
                    {
                        title: "Practical Versatility",
                        description: "Get the most out of your existing wardrobe with creative combinations you might never have considered."
                    },
                    {
                        title: "Weather-Ready Confidence",
                        description: "Always dressed appropriately for the weather, from sudden rain to unexpected heat."
                    },
                    {
                        title: "Style Evolution",
                        description: "Develop your personal style with AI-powered suggestions that learn from your preferences and feedback."
                    }
                ]
            },
            video: {
                title: "See Style Forecast in Action"
            },
            homeScreen: {
                title: "Add Style Forecast to Your Home Screen",
                description: "Get quick access to Style Forecast by adding it to your device's home screen. Choose your browser below:",
                chrome: "Chrome",
                safari: "Safari",
                steps: {
                    chrome: [
                        {
                            title: "Open Menu",
                            description: "Tap the three dots menu (⋮) in the top right corner of Chrome"
                        },
                        {
                            title: "Select \"Add to Home screen\"",
                            description: "Scroll down and tap on \"Add to Home screen\" option"
                        },
                        {
                            title: "Confirm",
                            description: "Tap \"Add\" in the popup that appears"
                        }
                    ],
                    safari: [
                        {
                            title: "Open Share Menu",
                            description: "Tap the share icon (square with arrow) at the bottom of your Safari browser"
                        },
                        {
                            title: "Scroll and Find \"Add to Home Screen\"",
                            description: "Scroll through the options and tap \"Add to Home Screen\""
                        },
                        {
                            title: "Confirm",
                            description: "You can edit the name if you want, then tap \"Add\" in the top right corner"
                        }
                    ]
                },
                footer: "After adding to your home screen, you'll be able to launch Style Forecast with a single tap, just like a native app!"
            },
            betaSection: {
                title: "Join the Style Revolution Early 🔥",
                subtitle: "Be among the first to experience the future of personal styling.",
                focusGroupTitle: "First focus group has started testing!",
                focusGroupDesc: "Our first focus group has already begun testing the Style Forecast app. Sign up now to join our second group of testers who will receive access soon.",
                focusGroupButton: "Join Second Focus Group"
            }
        },
        sr: {
            hero: {
                title: "Transformišite svoj stil uz personalizovanog stilista sa veštačkom inteligencijom",
                description: "Doživite svakodnevno stilizovanje bez napora uz AI asistenta koji uči vaš ukus, uzima u obzir vašu garderobu i osigurava da ste savršeno obučeni za bilo koje vreme ili priliku. Uštedite vreme i otključajte svoj stilski potencijal sa Style Forecast-om."
            },
            cta: "Isprobajte Style Forecast odmah",
            features: {
                title: "Zašto izabrati Style Forecast?",
                cards: [
                    {
                        icon: "🎯",
                        title: "Pametna integracija garderobe",
                        description: "Otpremite svoju postojeću odeću i dozvolite našoj veštačkoj inteligenciji da kreira beskonačne stilske kombinacije. Dobijte personalizovane predloge outfita koristeći svoju stvarnu garderobu, maksimalno iskorišćavajući svoju investiciju u odeću."
                    },
                    {
                        icon: "🌤️",
                        title: "Stilizovanje savršeno za vremenske uslove",
                        description: "Nikada više preobučeni ili nedovoljno obučeni. Naša veštačka inteligencija proverava podatke o vremenu u realnom vremenu i predlaže odgovarajuće outfite koji vas čine udobnim i stilizovanim u svim uslovima."
                    },
                    {
                        icon: "🎉",
                        title: "Outfiti spremni za svaku priliku",
                        description: "Od poslovnih sastanaka do opuštenih vikenda, dobijte trenutno odgovarajuće kombinacije outfita. Naša veštačka inteligencija razume kodove oblačenja i društvene kontekste kako bi osigurala da uvek izgledate savršeno za svaki događaj."
                    },
                    {
                        icon: "👕",
                        title: "Stilski asistent koji se razvija",
                        description: "Vaš lični AI stilista uči i prilagođava se vašim preferencijama tokom vremena. Delite povratne informacije, označite favorite i gledajte kako preporuke postaju sve više personalizovane prema vašem jedinstvenom ukusu."
                    }
                ]
            },
            howItWorks: {
                title: "Kako funkcioniše",
                steps: [
                    {
                        title: "Kreirajte svoj profil",
                        description: "Podelite svoje stilske preference, omiljene boje i modnu inspiraciju. Naša veštačka inteligencija uči vaš jedinstveni ukus kako bi kreirala personalizovane preporuke samo za vas."
                    },
                    {
                        title: "Dnevno planiranje stila",
                        description: "Primajte praktične kombinacije outfita svako jutro, savršeno usklađene sa vašim rasporedom i vremenskim uslovima. Planirajte svoje izglede unapred za važne događaje i nikada više ne stresirjte se oko toga šta da obučete."
                    },
                    {
                        title: "Kontinuirana evolucija stila",
                        description: "Uživajte u jutarnjoj rutini bez stresa sa savršeno koordiniranim outfitima. Dobijajte bolje preporuke kako koristite aplikaciju. Gledajte kako se vaš lični stil razvija uz uvide i predloge zasnovane na veštačkoj inteligenciji."
                    }
                ]
            },
            benefits: {
                title: "Transformišite svoje svakodnevno stilsko iskustvo",
                cards: [
                    {
                        title: "Rešenje koje štedi vreme",
                        description: "Započnite dan samopouzdano sa outfitima spremnim za nošenje. Bez više jutarnjeg stresa oko garderobe."
                    },
                    {
                        title: "Praktična svestranost",
                        description: "Izvucite najviše iz svoje postojeće garderobe sa kreativnim kombinacijama koje možda nikada ne biste razmotrili."
                    },
                    {
                        title: "Samopouzdanje za sve vremenske uslove",
                        description: "Uvek obučeni prikladno za vremenske uslove, od iznenadne kiše do neočekivane vrućine."
                    },
                    {
                        title: "Stilska evolucija",
                        description: "Razvijajte svoj lični stil uz predloge zasnovane na veštačkoj inteligenciji koji uče iz vaših preferenci i povratnih informacija."
                    }
                ]
            },
            video: {
                title: "Pogledajte Style Forecast u akciji"
            },
            homeScreen: {
                title: "Dodajte Style Forecast na početni ekran",
                description: "Dobijte brz pristup Style Forecast-u dodajući ga na početni ekran vašeg uređaja. Izaberite svoj pretraživač ispod:",
                chrome: "Chrome",
                safari: "Safari",
                steps: {
                    chrome: [
                        {
                            title: "Otvorite meni",
                            description: "Dodirnite meni sa tri tačke (⋮) u gornjem desnom uglu Chrome-a"
                        },
                        {
                            title: "Izaberite \"Dodaj na početni ekran\"",
                            description: "Skrolujte nadole i dodirnite opciju \"Dodaj na početni ekran\""
                        },
                        {
                            title: "Potvrdite",
                            description: "Dodirnite \"Dodaj\" u iskačućem prozoru koji se pojavi"
                        }
                    ],
                    safari: [
                        {
                            title: "Otvorite meni za deljenje",
                            description: "Dodirnite ikonu za deljenje (kvadrat sa strelicom) na dnu vašeg Safari pretraživača"
                        },
                        {
                            title: "Pronađite \"Dodaj na početni ekran\"",
                            description: "Skrolujte kroz opcije i dodirnite \"Dodaj na početni ekran\""
                        },
                        {
                            title: "Potvrdite",
                            description: "Možete urediti ime ako želite, zatim dodirnite \"Dodaj\" u gornjem desnom uglu"
                        }
                    ]
                },
                footer: "Nakon dodavanja na vaš početni ekran, moći ćete da pokrenete Style Forecast jednim dodirom, baš kao i nativnu aplikaciju!"
            },
            betaSection: {
                title: "Pridružite se stilskoj revoluciji rano 🔥",
                subtitle: "Budite među prvima koji će iskusiti budućnost personalnog stilizovanja.",
                focusGroupTitle: "Prva fokus grupa je počela sa testiranjem!",
                focusGroupDesc: "Naša prva fokus grupa je već počela sa testiranjem Style Forecast aplikacije. Prijavite se sada da se pridružite našoj drugoj grupi testera koji će uskoro dobiti pristup.",
                focusGroupButton: "Pridružite se drugoj fokus grupi"
            }
        }
    };

    // Get current translation based on language
    const t = translations[language] || translations.en;

    return (
        <div className="about-page">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-content">
                    <h1>{t.hero.title}</h1>
                    <p>{t.hero.description}</p>
                    <div className="social-icons">
                        <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                            <i className="fab fa-instagram"></i>
                        </a>
                        <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                            <i className="fab fa-linkedin"></i>
                        </a>
                        <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                            <i className="fab fa-youtube"></i>
                        </a>
                    </div>
                    <Link to="/" className="cta-button">{t.cta}</Link>
                </div>
                <div className="hero-animation">
                    <img src={aboutImage} alt="AI Style Assistant" />
                </div>
            </section>

            {/* Features Section */}
            <section className="features">
                <h2 className="section-title">{t.features.title}</h2>
                <div className="features-grid">
                    {t.features.cards.map((card, index) => (
                        <div className="feature-card" key={index}>
                            <div className="feature-icon">{card.icon}</div>
                            <h3>{card.title}</h3>
                            <p>{card.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works">
                <h2 className="section-title">{t.howItWorks.title}</h2>
                <div className="steps">
                    <div className="step">
                        <img src={first} alt={t.howItWorks.steps[0].title} />
                        <div className="step-number">1</div>
                        <h3>{t.howItWorks.steps[0].title}</h3>
                        <p>{t.howItWorks.steps[0].description}</p>
                    </div>
                    <div className="step">
                        <img src={second} alt={t.howItWorks.steps[1].title} />
                        <div className="step-number">2</div>
                        <h3>{t.howItWorks.steps[1].title}</h3>
                        <p>{t.howItWorks.steps[1].description}</p>
                    </div>
                    <div className="step">
                        <img src={third} alt={t.howItWorks.steps[2].title} />
                        <div className="step-number">3</div>
                        <h3>{t.howItWorks.steps[2].title}</h3>
                        <p>{t.howItWorks.steps[2].description}</p>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="benefits">
                <h2 className="section-title">{t.benefits.title}</h2>
                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3>
                            <i className="fa-solid fa-clock benefit-icon"></i>
                            {t.benefits.cards[0].title}
                        </h3>
                        <p>{t.benefits.cards[0].description}</p>
                    </div>
                    <div className="benefit-card">
                        <h3>
                            <i className="fa-solid fa-rotate benefit-icon"></i>
                            {t.benefits.cards[1].title}
                        </h3>
                        <p>{t.benefits.cards[1].description}</p>
                    </div>
                    <div className="benefit-card">
                        <h3>
                            <i className="fa-solid fa-cloud-sun benefit-icon"></i>
                            {t.benefits.cards[2].title}
                        </h3>
                        <p>{t.benefits.cards[2].description}</p>
                    </div>
                    <div className="benefit-card">
                        <h3>
                            <i className="fa-solid fa-arrow-trend-up benefit-icon"></i>
                            {t.benefits.cards[3].title}
                        </h3>
                        <p>{t.benefits.cards[3].description}</p>
                    </div>
                </div>
            </section>

            {/* Video Section */}
            <section className="video">
                <h2 className="section-title">{t.video.title}</h2>
                <div className="video-container">
                    <iframe
                        src="https://www.youtube.com/embed/NkvUSyDv6ug?autoplay=1&mute=1"
                        title="Style Forecast Demo"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen>
                    </iframe>
                </div>
            </section>

            {/* Add to Home Screen Section */}
            <section className="shortcut-guide-section">
                <div className="shortcut-container">
                    <h2 className="section-title">{t.homeScreen.title}</h2>
                    <p className="shortcut-description">
                        {t.homeScreen.description}
                    </p>

                    {/* Browser Tabs */}
                    <div className="browser-tabs">
                        <button className="browser-tab active" data-browser="chrome">
                            <img src="https://cdnjs.cloudflare.com/ajax/libs/browser-logos/73.0.0/chrome/chrome_32x32.png" alt="Chrome logo" />
                            <span>{t.homeScreen.chrome}</span>
                        </button>
                        <button className="browser-tab" data-browser="safari">
                            <img src="https://cdnjs.cloudflare.com/ajax/libs/browser-logos/73.0.0/safari/safari_32x32.png" alt="Safari logo" />
                            <span>{t.homeScreen.safari}</span>
                        </button>
                    </div>

                    {/* Chrome Instructions */}
                    <div className="browser-instructions" id="chrome-instructions">
                        <div className="instructions-grid">
                            {t.homeScreen.steps.chrome.map((step, index) => (
                                <div className="instruction-step" key={index}>
                                    <div className="step-number">{index + 1}</div>
                                    <div className="step-content">
                                        <h3>{step.title}</h3>
                                        <p>{step.description}</p>
                                        <div className="step-image">
                                            <img src={[step1, step2, step3][index]} alt={step.title} className="instruction-img" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Safari Instructions */}
                    <div className="browser-instructions" id="safari-instructions" style={{ display: 'none' }}>
                        <div className="instructions-grid">
                            {t.homeScreen.steps.safari.map((step, index) => (
                                <div className="instruction-step" key={index}>
                                    <div className="step-number">{index + 1}</div>
                                    <div className="step-content">
                                        <h3>{step.title}</h3>
                                        <p>{step.description}</p>
                                        <div className="step-image">
                                            <img src={[step4, step5, step3][index]} alt={step.title} className="instruction-img" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="shortcut-footer">
                        <p>{t.homeScreen.footer}</p>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="countdown-section">
                <div className="beta-info-container">
                    <h2>{t.betaSection.title}</h2>
                    <div className="subtitle">{t.betaSection.subtitle}</div>
                    
                    {/* Focus Group Info Box */}
                    <div className="beta-info-box">
                        <div className="beta-text-content">
                            <h3 className="beta-title" id="focusGroupTitle">{t.betaSection.focusGroupTitle}</h3>
                            <p className="beta-description" id="focusGroupDesc">{t.betaSection.focusGroupDesc}</p>
                            <Link to="https://docs.google.com/forms/d/e/1FAIpQLSeJqYz6Trne1TeeoP5shfFnPGJVAeDg6XPgHi98hkKnLQciWg/viewform?usp=header" className="beta-cta-button" id="focusGroupButton">{t.betaSection.focusGroupButton}</Link>
                        </div>
                        <div className="beta-image">
                            <img src="https://cdn.pixabay.com/photo/2018/03/10/09/45/businessman-3213659_1280.jpg" alt="People testing Style Forecast app" id="betaTestingImage" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Browser Tabs Component */}
            <AboutBrowserTabs />
        </div>
    );
}

export default About;